/**
 * Tranquira bra PDP - vanilla ES module (entry). Each feature self-initialises
 * from a data-pdp-* attribute so sections stay independent. Only the
 * above-the-fold features live here; pdp-below.js and pdp-overlays.js are
 * imported lazily (see "lazy module loading" at the bottom). Cart mutations that need
 * the drawer go through the existing cart-drawer.js (form submit interception
 * and [data-open-cart] delegation) - this file never re-implements the drawer.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ money */

function formatMoney(cents, format) {
  const fmt = format || window.themeSettings?.moneyFormat || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  const [whole, dec] = value.split('.');
  const withThousands = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return fmt
    .replace(/\{\{\s*amount\s*\}\}/g, `${withThousands(',')}.${dec}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withThousands(','))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${withThousands('.')},${dec}`)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withThousands('.'));
}

/* announcement rotation, copy-code and toast moved into
   sections/pdp-announcement.liquid - the bar renders on every template now,
   including pages that never load this module */

/* -------------------------------------------------------------- segmented */

function segSelect(group, btn, notify = true) {
  group.querySelectorAll('.pdp-seg__btn').forEach((b) => {
    const on = b === btn;
    b.setAttribute('aria-checked', String(on));
    b.tabIndex = on ? 0 : -1;
  });
  if (notify) {
    group.dispatchEvent(new CustomEvent('pdp:segmented', {
      bubbles: true,
      detail: { name: group.dataset.name, value: btn.dataset.value, group }
    }));
  }
}

function initSegmented(group) {
  group.addEventListener('click', (e) => {
    const btn = e.target.closest('.pdp-seg__btn');
    if (btn && !btn.disabled) segSelect(group, btn);
  });
  group.addEventListener('keydown', (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const btns = [...group.querySelectorAll('.pdp-seg__btn:not(:disabled)')];
    const current = group.querySelector('.pdp-seg__btn[aria-checked="true"]');
    let i = btns.indexOf(document.activeElement);
    if (i === -1) i = btns.indexOf(current);
    const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
    const next = btns[(i + dir + btns.length) % btns.length];
    if (next) { next.focus(); segSelect(group, next); }
  });
}

/* ------------------------------------------------------------- session */

// ?gift_test=1 on an UNPUBLISHED theme sets the same session override as the console would
// (phone previews); the live theme ignores it - both the Liquid gate and every reader check the role.
try {
  if (new URLSearchParams(window.location.search).get('gift_test') === '1' && window.Shopify?.theme?.role !== 'main') sessionStorage.setItem('tranquira:gift-test', '1');
} catch (e) {}
function sessionFlag(key) {
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
}

/* ---------------------------------------------------------------- buy box */

function initBuyBox(root) {
  // once per DOM node: the theme editor re-renders the section on every setting change and the
  // new node arrives without handlers (see the shopify:section:load listener at the bottom)
  if (root.dataset.pdpInit === '1') return;
  root.dataset.pdpInit = '1';
  const variantsEl = root.querySelector('[data-pdp-variants]');
  const optionsEl = root.querySelector('[data-pdp-options]');
  if (!variantsEl) return;
  let variants = [];
  let options = [];
  try {
    variants = JSON.parse(variantsEl.textContent);
    options = JSON.parse(optionsEl?.textContent || '[]');
  } catch { return; }

  const strings = window.themeStrings || {};
  // surprise-gift copy: hidden for the session once the drawer found the
  // gift not free (tranquira:gift-off); revealed on unpublished themes only
  // by the test override (tranquira:gift-test) - see snippets/bundle-gift-flag
  root.querySelectorAll('[data-gift-ui]').forEach((el) => {
    if (sessionFlag('tranquira:gift-off')) { el.hidden = true; return; }
    if (el.hidden && el.dataset.giftTestable === '1' && sessionFlag('tranquira:gift-test') && window.Shopify?.theme?.role !== 'main') el.hidden = false;
  });
  document.addEventListener('tranquira:gift-off', () => {
    root.querySelectorAll('[data-gift-ui]').forEach((el) => { el.hidden = true; });
  });
  const colorName = root.dataset.colorOption;
  const sizeName = root.dataset.sizeOption;
  const posOf = (name) => options.find((o) => o.name === name)?.position || 0;
  const selected = {};

  // seed from segmented groups (colour swatches) and the size select
  root.querySelectorAll('[data-picker] [data-pdp-segmented]').forEach((group) => {
    const checked = group.querySelector('.pdp-seg__btn[aria-checked="true"]');
    if (checked) selected[group.dataset.name] = checked.dataset.value;
  });
  const sizeSelect = root.querySelector('[data-size-select]');
  if (sizeSelect) selected[sizeSelect.dataset.name] = sizeSelect.value;
  // no size chosen yet for one piece: the form carries no id, and every
  // add-to-cart path (button, sticky bar, header bar) lands on the picker
  const sizeMissing = () => !!(sizeName && sizeSelect && !selected[sizeName]);
  const sizeGroup = sizeSelect?.closest('[data-picker]') || null;
  const sizeErrorEl = sizeGroup?.querySelector('[data-size-error]') || null;
  const showSingleSizeError = () => {
    if (!sizeGroup) return;
    const wrap = sizeGroup.querySelector('.pdp-select-wrap');
    wrap?.classList.add('is-invalid');
    sizeGroup.classList.remove('pdp-picker-pulse');
    requestAnimationFrame(() => sizeGroup.classList.add('pdp-picker-pulse'));
    if (sizeErrorEl) sizeErrorEl.hidden = false;
    sizeGroup.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
    sizeSelect?.focus({ preventScroll: true });
  };
  const clearSingleSizeError = () => {
    sizeGroup?.querySelector('.pdp-select-wrap')?.classList.remove('is-invalid');
    sizeGroup?.classList.remove('pdp-picker-pulse');
    if (sizeErrorEl) sizeErrorEl.hidden = true;
  };

  const optionValues = (variant) => [variant.option1, variant.option2, variant.option3];
  const matches = (variant) =>
    options.every((o) => {
      const want = selected[o.name];
      return !want || optionValues(variant)[o.position - 1] === want;
    });

  const currentVariant = () => variants.find(matches) || null;

  /* ---- bundle tier picker: 1 / 2 / 3 pieces, the tier is the quantity ----
     Tier 3 is the store's Buy 2 Get 1 automatic discount: three full-price
     line items in one POST /cart/add.js, the third repriced at checkout.
     The bundle maths always compares against the real unit price. */
  const tiersEl = root.querySelector('[data-pdp-tiers]');
  const cards = tiersEl ? [...tiersEl.querySelectorAll('[data-tier]')] : [];
  const rowsWrap = root.querySelector('[data-tier-rows]');
  const rows = rowsWrap ? [...rowsWrap.querySelectorAll('[data-bundle-row]')] : [];
  const dividers = rowsWrap ? [...rowsWrap.querySelectorAll('[data-bundle-divider]')] : [];
  const tier2Total = parseInt(tiersEl?.dataset.tier2Total, 10) || 0;
  let tier = cards.length
    ? (parseInt(cards.find((c) => c.getAttribute('aria-checked') === 'true')?.dataset.tier, 10) || 1)
    : 1;
  root.dataset.tier = String(tier);

  // what a tier costs at a given unit price (cents)
  const tierMath = (t, unit) => {
    if (t === 3) {
      const total = unit * 2;
      return { count: 3, total, full: unit * 3, perUnit: Math.round(total / 3), save: unit * 3 - total };
    }
    if (t === 2) {
      const total = tier2Total > 0 && tier2Total < unit * 2 ? tier2Total : unit * 2;
      return { count: 2, total, full: unit * 2, perUnit: Math.round(total / 2), save: unit * 2 - total };
    }
    return { count: 1, total: unit, full: unit, perUnit: unit, save: 0 };
  };

  // colour -> close-up thumb map, rendered server-side by the snippet
  let thumbMap = {};
  try {
    thumbMap = JSON.parse(tiersEl?.querySelector('[data-bundle-thumbs]')?.textContent || '{}');
  } catch { /* thumbs stay on their initial render */ }

  const setRowThumb = (row, color) => {
    const thumb = row.querySelector('[data-row-thumb]');
    const entry = thumbMap[color];
    if (!thumb) return;
    const wrap = thumb.closest('[data-row-thumbwrap]');
    if (entry && entry.src) {
      thumb.src = entry.src;
      thumb.srcset = `${entry.src} 1x, ${entry.src2x} 2x`;
      wrap?.classList.remove('pdp-bundle__thumbwrap--empty');
    } else {
      // this colour has no variant image: neutral placeholder, never another colour's photo
      thumb.removeAttribute('src'); thumb.removeAttribute('srcset');
      wrap?.classList.add('pdp-bundle__thumbwrap--empty');
    }
  };

  /* ---- sizes: every row starts empty; the FIRST size picked anywhere
     (a row, or the tier-1 picker before switching to 2+1) fills the rows
     still empty, then every row is independent. */
  let firstPickDone = false;
  let sizeChosen = false; // the shopper picked a size in the tier-1 picker

  const rowSelection = (row) => {
    const sel = {};
    row.querySelectorAll('[data-row-option]').forEach((el) => {
      sel[el.dataset.rowOption] = el.value;
    });
    return sel;
  };

  // option positions come from the buy box's locale-aware option names, so
  // /it (Colore / Taglia) resolves the same variants as /en
  const findRowVariant = (sel) => {
    const colorPos = posOf(colorName);
    const sizePos = posOf(sizeName);
    return variants.find((v) => {
      const opts = optionValues(v);
      if (colorPos && sel.color && opts[colorPos - 1] !== sel.color) return false;
      if (sizePos && sel.size && opts[sizePos - 1] !== sel.size) return false;
      return true;
    }) || null;
  };

  const activeRows = () => rows.slice(0, tier);

  // a row's size options mirror the single picker's sold-out marking for
  // the row's own colour
  const refreshRowSoldOut = (row) => {
    const sizePos = posOf(sizeName);
    const colorPos = posOf(colorName);
    const sizeSel = row.querySelector('select[data-row-option="size"]');
    if (!sizeSel || !sizePos) return;
    const color = row.querySelector('select[data-row-option="color"]')?.value;
    [...sizeSel.options].forEach((opt) => {
      if (!opt.value) return; // the "choose a size" placeholder
      const anyAvailable = variants.some((v) => {
        const opts = optionValues(v);
        if (opts[sizePos - 1] !== opt.value || !v.available) return false;
        return !colorPos || !color || opts[colorPos - 1] === color;
      });
      opt.disabled = !anyAvailable;
      opt.textContent = anyAvailable ? opt.value : `${opt.value} - ${strings.soldOut?.toLowerCase() || 'sold out'}`;
    });
  };
  const rowSizeSelects = () => rows.map((row) => row.querySelector('select[data-row-option="size"]')).filter(Boolean);
  const rowsAvailable = () => activeRows().every((row) => {
    const sel = rowSelection(row);
    // no size yet: the button stays live and submit asks for one
    if (!sel.size) return true;
    const v = findRowVariant(sel);
    return !!(v && v.available);
  });

  // one-line hint after the fill, gone after ~4s (instantly under reduced motion)
  const hintEl = rowsWrap?.querySelector('[data-fill-hint]');
  let hintTimer = 0;
  const showFillHint = () => {
    if (!hintEl) return;
    clearTimeout(hintTimer);
    hintEl.classList.remove('is-fading');
    hintEl.hidden = false;
    hintTimer = setTimeout(() => {
      if (REDUCED_MOTION.matches) { hintEl.hidden = true; return; }
      hintEl.classList.add('is-fading');
      hintTimer = setTimeout(() => { hintEl.hidden = true; hintEl.classList.remove('is-fading'); }, 450);
    }, 4000);
  };

  // the first size picked fills every row whose size is still empty; rows
  // set by hand are never overwritten
  const fillEmptySizes = (size) => {
    if (!size) return false;
    let filled = 0;
    rowSizeSelects().forEach((s) => {
      if (s.value) return;
      if ([...s.options].some((o) => o.value === size)) {
        s.value = size;
        s.closest('.pdp-bundle__select-wrap')?.classList.remove('is-invalid');
        filled += 1;
      }
    });
    firstPickDone = true;
    // every row has a size now: the "choose a size" message has done its job
    if (errorEl && !errorEl.hidden && errorEl.textContent === errorEl.dataset.sizeRequired && rowSizeSelects().every((s) => s.value)) errorEl.hidden = true;
    return filled > 0;
  };

  function renderRows(unit) {
    if (!rowsWrap || tier < 2) return;
    const m = tierMath(tier, unit);
    // two at full price, one free: rows 1 and 2 print the unit price, the
    // third row prints the unit price struck through and then GRATIS
    const freeIndex = tier === 3 ? 2 : -1;
    const freeLabel = tiersEl?.dataset.freeLabel || 'FREE';
    activeRows().forEach((row, i) => {
      const sel = rowSelection(row);
      if (sel.color) setRowThumb(row, sel.color);
      const isFree = i === freeIndex;
      const priceEl = row.querySelector('[data-row-price]');
      const compareEl = row.querySelector('[data-row-compare]');
      if (priceEl) {
        priceEl.textContent = isFree ? freeLabel : formatMoney(unit);
        priceEl.classList.toggle('pdp-bundle__price--free', isFree);
      }
      if (compareEl) compareEl.textContent = isFree ? formatMoney(unit) : '';
    });
    // "one in each colour" is only true while the rows still hold distinct colours
    const note = tiersEl?.querySelector('[data-tier="3"] .pdp-tier__note');
    if (note) {
      const colours = new Set(activeRows().map((row) => rowSelection(row).color));
      note.hidden = tier === 3 && colours.size < activeRows().length;
    }
    // "2 x unit + 1 free", then the tier total bold with the full price struck
    const lineEl = rowsWrap.querySelector('[data-bundle-line]');
    if (lineEl) {
      const tmpl = tier === 3 ? (lineEl.dataset.lineFree || '%s') : (lineEl.dataset.linePieces || '%n x %s').replace('%n', String(m.count));
      lineEl.textContent = tmpl.replace('%s', formatMoney(unit));
    }
    const fullEl = rowsWrap.querySelector('[data-bundle-full]');
    if (fullEl) fullEl.textContent = formatMoney(m.full);
    const totalEl = rowsWrap.querySelector('[data-bundle-total]');
    if (totalEl) totalEl.textContent = formatMoney(m.total);
  }

  const touched = new Set();
  rowsWrap?.addEventListener('change', (e) => {
    const select = e.target.closest('select[data-row-option]');
    if (!select) return;
    const row = select.closest('[data-bundle-row]');
    const rowIndex = rows.indexOf(row);
    const kind = select.dataset.rowOption;
    touched.add(`${rowIndex}:${kind}`);
    if (kind === 'size') {
      select.closest('.pdp-bundle__select-wrap')?.classList.remove('is-invalid');
      if (!firstPickDone && select.value) {
        if (fillEmptySizes(select.value)) showFillHint();
      }
    }
    refresh();
  });

  function applyTier(next) {
    const prev = tier;
    tier = next;
    root.dataset.tier = String(tier);
    const multi = tier > 1;
    // carry the shopper's picks across: the single pickers seed the rows
    // nobody touched yet, row 1 seeds the single pickers on the way back
    if (prev === 1 && multi) {
      // the chosen colour leads and the other colours follow, so the rows
      // still hold one of each colour
      const chosen = colorName ? selected[colorName] : null;
      const firstColorSel = rows[0]?.querySelector('select[data-row-option="color"]');
      const palette = firstColorSel ? [...firstColorSel.options].map((o) => o.value) : [];
      const order = chosen && palette.includes(chosen) ? [chosen, ...palette.filter((c) => c !== chosen)] : palette;
      rows.forEach((row, i) => {
        const colorSel = row.querySelector('select[data-row-option="color"]');
        if (colorSel && order.length && !touched.has(`${i}:color`)) colorSel.value = order[i % order.length];
      });
      // a size picked for one piece counts as the first pick for three
      if (!firstPickDone && sizeChosen && sizeName && selected[sizeName]) {
        if (fillEmptySizes(selected[sizeName])) showFillHint();
      }
    } else if (prev > 1 && !multi && rows[0]) {
      const sel = rowSelection(rows[0]);
      if (colorName && sel.color) {
        selected[colorName] = sel.color;
        const swatchGroup = root.querySelector('[data-picker="color"] [data-pdp-segmented]');
        const btn = swatchGroup?.querySelector(`.pdp-seg__btn[data-value="${CSS.escape(sel.color)}"]`);
        if (swatchGroup && btn) segSelect(swatchGroup, btn, false);
        const colorLabel = root.querySelector('[data-picker="color"] [data-picker-value]');
        if (colorLabel) colorLabel.textContent = sel.color;
      }
      if (sizeName && sel.size && sizeSelect) {
        selected[sizeName] = sel.size;
        sizeSelect.value = sel.size;
        setPickerLabel(sel.size);
        sizeChosen = true;
      }
    }
    cards.forEach((c) => {
      const on = parseInt(c.dataset.tier, 10) === tier;
      c.setAttribute('aria-checked', String(on));
      c.tabIndex = on ? 0 : -1;
    });
    if (rowsWrap) rowsWrap.hidden = !multi;
    rows.forEach((row, i) => { row.hidden = !multi || i >= tier; });
    dividers.forEach((d, i) => { d.hidden = !multi || i + 1 >= tier; });
    // the single colour + size pickers only make sense for one piece
    root.querySelectorAll('[data-picker]').forEach((g) => { g.hidden = multi; });
    refresh();
  }

  tiersEl?.addEventListener('click', (e) => {
    // .pdp-tier, not [data-tier]: the buy box root carries data-tier as state
    const card = e.target.closest('.pdp-tier');
    if (!card) return;
    const next = parseInt(card.dataset.tier, 10);
    if (next && next !== tier) applyTier(next);
  });
  tiersEl?.addEventListener('keydown', (e) => {
    // only the radio cards: the per-item rows' selects live inside tiersEl
    // too and need their arrow keys for their own values
    if (!e.target.closest('.pdp-tier')) return;
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    const i = cards.findIndex((c) => c.getAttribute('aria-checked') === 'true');
    if (i === -1) return;
    e.preventDefault();
    const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
    const next = cards[(i + dir + cards.length) % cards.length];
    next.focus();
    applyTier(parseInt(next.dataset.tier, 10));
  });

  function refreshSoldOut() {
    if (!sizeName || !sizeSelect) return;
    const sizePos = posOf(sizeName);
    if (!sizePos) return;
    [...sizeSelect.options].forEach((opt) => {
      const value = opt.value;
      // the "choose a size" placeholder is never a size: leave its label alone and keep it selectable
      if (!value) { opt.disabled = false; opt.textContent = root.querySelector('[data-picker="size"]')?.dataset.chooseSize || opt.textContent; return; }
      const anyAvailable = variants.some((v) => {
        if (optionValues(v)[sizePos - 1] !== value || !v.available) return false;
        if (colorName && selected[colorName]) {
          return optionValues(v)[posOf(colorName) - 1] === selected[colorName];
        }
        return true;
      });
      opt.disabled = !anyAvailable;
      const base = value;
      opt.textContent = anyAvailable ? base : `${base} - ${strings.soldOut?.toLowerCase() || 'sold out'}`;
    });
  }

  function refresh() {
    const variant = currentVariant();
    const idInput = root.querySelector('[data-variant-id]');
    const atc = root.querySelector('[data-pdp-atc]');
    const priceEl = root.querySelector('[data-price]');
    const compareEl = root.querySelector('[data-compare-price]');
    const savePill = root.querySelector('[data-save-pill]');
    const savePct = root.querySelector('[data-save-pct]');
    const multi = tier > 1;
    if (multi) activeRows().forEach(refreshRowSoldOut);

    if (variant) {
      const missing = !multi && sizeMissing();
      // two or three pieces never travel as the hidden id: the rows' variants go as JSON items
      // (submit handler below). An empty id means a raw form post can add nothing wrong.
      if (idInput) idInput.value = missing || multi ? '' : variant.id;
      const m = tierMath(tier, variant.price);
      // multi-piece availability comes from the rows, not the hidden pickers
      const available = multi ? rowsAvailable() : !!variant.available;
      const priceNow = multi ? m.total : variant.price;
      const priceWas = multi ? m.full : (variant.compare_at_price || 0);
      const hasCompare = priceWas > priceNow;
      if (priceEl) priceEl.textContent = formatMoney(priceNow);
      if (compareEl) {
        compareEl.textContent = hasCompare ? formatMoney(priceWas) : '';
        compareEl.classList.toggle('hidden', !hasCompare);
      }
      // save pill: a money amount for a tier (the discount is a fixed
      // amount), the percent template for the single-piece compare-at
      if (savePill && savePct) {
        if (multi && m.save > 0) {
          savePct.textContent = (savePill.dataset.amountTemplate || '%s').replace('%s', formatMoney(m.save));
          savePill.classList.remove('hidden');
        } else if (!multi && hasCompare) {
          const pct = Math.round(((priceWas - priceNow) / priceWas) * 100);
          savePct.textContent = (savePill.dataset.pctTemplate || '%d%').replace('%d', String(pct));
          savePill.classList.remove('hidden');
        } else {
          savePill.classList.add('hidden');
        }
      }
      if (atc) {
        atc.disabled = !available;
        const label = atc.querySelector('.pdp-buy__atc-label');
        if (label) {
          if (!available) {
            label.textContent = strings.soldOut || 'Sold out';
          } else if (multi) {
            label.textContent = (atc.dataset.labelN || '%n x %s')
              .replace('%n', String(m.count))
              .replace('%s', formatMoney(m.total));
          } else if (atc.dataset.labelOne) {
            label.textContent = atc.dataset.labelOne.replace('%s', formatMoney(variant.price));
          } else {
            label.textContent = strings.addToCart || 'Add to cart';
          }
        }
      }
      // sticky bar and header product bar mirror the selected tier: price,
      // compare, save pill; the single-variant select only for one piece.
      // Both bars submit this same product form.
      const stickyPrice = document.querySelector('[data-sticky-price]');
      const stickyCompare = document.querySelector('[data-sticky-compare]');
      const stickySave = document.querySelector('[data-sticky-save]');
      // the sticky thumb follows the colour on show: the single picker for one piece, the FIRST row for 2+1
      const stickyThumb = document.querySelector('[data-sticky-thumb]');
      if (stickyThumb && colorName) {
        const rowColor = multi && rows[0] ? rowSelection(rows[0]).color : '';
        const e = thumbMap[rowColor || selected[colorName]];
        const wrap = stickyThumb.closest('[data-sticky-thumbwrap]');
        if (e && e.src) { stickyThumb.src = e.src; stickyThumb.srcset = `${e.src} 1x, ${e.src2x} 2x`; wrap?.classList.remove('pdp-sticky__thumbwrap--empty'); }
        else { stickyThumb.removeAttribute('src'); stickyThumb.removeAttribute('srcset'); wrap?.classList.add('pdp-sticky__thumbwrap--empty'); }
      }
      const stickySelect = document.querySelector('[data-sticky-variant]');
      if (stickyPrice) stickyPrice.textContent = formatMoney(priceNow);
      if (stickyCompare) {
        stickyCompare.textContent = hasCompare ? formatMoney(priceWas) : '';
        stickyCompare.hidden = !hasCompare;
      }
      if (stickySave) {
        if (multi && m.save > 0) {
          stickySave.textContent = (savePill?.dataset.amountTemplate || '%s').replace('%s', formatMoney(m.save));
          stickySave.hidden = false;
        } else if (!multi && hasCompare) {
          const pct = Math.round(((priceWas - priceNow) / priceWas) * 100);
          stickySave.textContent = (savePill?.dataset.pctTemplate || '%d%').replace('%d', String(pct));
          stickySave.hidden = false;
        } else {
          stickySave.hidden = true;
        }
      }
      if (stickySelect) {
        stickySelect.closest('.pdp-sticky__variant')?.toggleAttribute('hidden', multi);
        const want = missing ? '' : String(variant.id);
        if (String(stickySelect.value) !== want) stickySelect.value = want;
      }
      // sticky title follows the tier: "Sollevia · 2+1" on the offer tier,
      // "Sollevia · 1" for one piece, "Sollevia · <n>" for any other count
      const stickyJump = document.querySelector('[data-sticky-jump]');
      if (stickyJump) {
        const count = multi && m ? Number(m.count) : 1;
        const base = stickyJump.dataset.titleBase || '';
        const suffix = count >= 3 ? (stickyJump.dataset.offerTitle || String(count)) : String(count);
        const titleEl = stickyJump.querySelector('[data-sticky-title]');
        if (titleEl) titleEl.textContent = `${base} · ${suffix}`;
      }
      document.querySelectorAll('[data-header-price]').forEach((el) => { el.textContent = formatMoney(priceNow); });
      // sticky-bar gift line follows the tier: an invitation on one piece, a static note on 2+1
      const stickyGift = document.querySelector('[data-sticky-gift]');
      if (stickyGift) {
        const isOffer = tier >= 3;
        const text = stickyGift.querySelector('[data-sticky-gift-text]');
        const label = isOffer ? (stickyGift.dataset.labelBundle || '') : (stickyGift.dataset.labelSingle || '');
        if (text) text.textContent = label;
        stickyGift.setAttribute('aria-label', label);
        stickyGift.classList.toggle('is-static', isOffer);
        stickyGift.setAttribute('aria-disabled', String(isOffer));
        fitStickyGift(stickyGift);
      }
      renderRows(variant.price);
    } else if (atc) {
      atc.disabled = true;
      const label = atc.querySelector('.pdp-buy__atc-label');
      if (label) label.textContent = strings.unavailable || 'Unavailable';
    }
    refreshSoldOut();
  }

  root.addEventListener('pdp:segmented', (e) => {
    const { name, value, group } = e.detail;
    if (!options.some((o) => o.name === name)) return;
    selected[name] = value;
    const pickerLabel = group.closest('[data-picker]')?.querySelector('[data-picker-value]');
    if (pickerLabel) pickerLabel.textContent = value;
    refresh();
    // colour changed: bring that variant's image into view
    if (name === colorName) {
      const variant = currentVariant();
      const position = variant?.featured_image?.position;
      if (position) {
        document.dispatchEvent(new CustomEvent('pdp:gallery:show', { detail: { index: position - 1 } }));
      }
    }
  });

  const setPickerLabel = (value) => {
    const pickerLabel = sizeGroup?.querySelector('[data-picker-value]');
    if (!pickerLabel) return;
    pickerLabel.textContent = value || sizeGroup.dataset.chooseSize || '';
    pickerLabel.classList.toggle('pdp-buy__label-empty', !value);
  };
  if (sizeSelect) {
    sizeSelect.addEventListener('change', () => {
      selected[sizeSelect.dataset.name] = sizeSelect.value;
      setPickerLabel(sizeSelect.value);
      sizeChosen = !!sizeSelect.value;
      if (sizeSelect.value) clearSingleSizeError();
      refresh();
    });
  }

  // sticky-bar variant select: apply the picked variant back to the buy box
  function applyVariant(variant) {
    if (!variant) return;
    const prevColor = colorName ? selected[colorName] : null;
    options.forEach((o) => {
      selected[o.name] = optionValues(variant)[o.position - 1];
    });
    // swatch UI
    const swatchGroup = root.querySelector('[data-picker="color"] [data-pdp-segmented]');
    if (swatchGroup && colorName) {
      const btn = swatchGroup.querySelector(`.pdp-seg__btn[data-value="${CSS.escape(selected[colorName])}"]`);
      if (btn) segSelect(swatchGroup, btn, false);
      const label = root.querySelector('[data-picker="color"] [data-picker-value]');
      if (label) label.textContent = selected[colorName];
    }
    if (sizeSelect && sizeName) {
      sizeSelect.value = selected[sizeName];
      setPickerLabel(selected[sizeName]);
      sizeChosen = !!selected[sizeName];
      if (selected[sizeName]) clearSingleSizeError();
    }
    refresh();
    if (colorName && selected[colorName] !== prevColor) {
      const position = variant.featured_image?.position;
      if (position) {
        document.dispatchEvent(new CustomEvent('pdp:gallery:show', { detail: { index: position - 1 } }));
      }
    }
  }
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-sticky-variant]');
    if (!sel) return;
    applyVariant(variants.find((v) => String(v.id) === String(sel.value)));
  });
  // sticky title / sub-line tap: smooth-scroll to the tier picker
  document.addEventListener('click', (e) => {
    const jump = e.target.closest('[data-sticky-jump]');
    if (!jump) return;
    const target = document.querySelector('.pdp-tiers');
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  });
  // gift copy taps (buy-box line, sticky-bar line): select the 2+1 card and scroll to it
  document.addEventListener('click', (e) => {
    const jump = e.target.closest('[data-gift-jump]');
    if (!jump || jump.classList.contains('is-static')) return;
    const offer = cards.find((c) => c.dataset.tier === '3');
    if (offer && tier !== 3) applyTier(3);
    const target = document.querySelector('.pdp-tiers');
    if (target) target.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
    // the answer is visible right away: the 2+1 card's gift line (and the buy-box gift line) flash briefly
    const flashEls = [...document.querySelectorAll('.pdp-tier[data-tier="3"] .pdp-tier__ribbon, .pdp-buy__giftline')].filter((el) => !el.hidden);
    // restart the animation across a frame instead of forcing a reflow (void offsetWidth)
    flashEls.forEach((el) => el.classList.remove('pdp-gift-flash'));
    requestAnimationFrame(() => flashEls.forEach((el) => el.classList.add('pdp-gift-flash')));
    setTimeout(() => flashEls.forEach((el) => el.classList.remove('pdp-gift-flash')), 1400);
  });

  /* the sticky gift chip must show its text in full at 360, 390 and 430px: shrink first
     (is-compact), then icon only (the label stays on aria-label). Re-checked whenever the
     chip is laid out again (the sticky bar appears on scroll) and on resize. */
  function fitStickyGift(chip) {
    if (!chip || chip.hidden || !chip.offsetParent) return;
    chip.classList.remove('is-compact', 'is-icon-only');
    // the chip hugs its text (justify-self: start, max-width 100%): its own scrollWidth says whether the text fits
    const overflows = () => chip.scrollWidth > chip.clientWidth + 1;
    if (!overflows()) return;
    chip.classList.add('is-compact');
    if (overflows()) chip.classList.add('is-icon-only');
  }
  (() => {
    const chip = document.querySelector('[data-sticky-gift]');
    if (!chip) return;
    if ('ResizeObserver' in window) new ResizeObserver(() => fitStickyGift(chip)).observe(chip);
    window.addEventListener('resize', () => fitStickyGift(chip));
  })();

  /* ---- add to cart ----
     One piece: the plain product form, intercepted by cart-drawer.js
     (FormData -> /cart/add.js). Two or three pieces: the rows' variants go
     as separate full-price line items in ONE POST /cart/add.js - the
     retired bundle card's exact mechanism - and the store's Buy X Get Y
     automatic discount reprices them at checkout; the drawer then opens
     through [data-open-cart], so cart-drawer.js stays the only drawer
     controller. The sticky bar submits this same form. */
  const form = root.querySelector('form[action*="/cart/add"]');
  const atcBtn = root.querySelector('[data-pdp-atc]');
  const errorEl = root.querySelector('[data-bundle-error]');
  let submitting = false;
  const errorDefault = errorEl?.textContent || '';
  // rows without a size: keep the shopper on those rows instead of an
  // alert - the picker pulse and an accent ring on each empty size select,
  // the message in the error box
  const showSizeError = (emptyRows) => {
    emptyRows.forEach((row, i) => {
      const wrap = row.querySelector('select[data-row-option="size"]')?.closest('.pdp-bundle__select-wrap');
      if (!wrap) return;
      wrap.classList.add('is-invalid');
      wrap.classList.remove('pdp-picker-pulse');
      requestAnimationFrame(() => wrap.classList.add('pdp-picker-pulse'));
      if (i === 0) {
        wrap.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'center' });
        wrap.querySelector('select')?.focus({ preventScroll: true });
      }
    });
    if (errorEl) {
      errorEl.textContent = errorEl.dataset.sizeRequired || errorDefault;
      errorEl.hidden = false;
    }
  };
  const clearLoading = () => {
    submitting = false;
    atcBtn?.classList.remove('is-loading');
    atcBtn?.querySelector('.pdp-buy__atc-spinner')?.setAttribute('hidden', '');
  };
  // loading state: cleared when the drawer opens (cart-drawer.js) or after 8s
  const setLoading = () => {
    if (!atcBtn) return;
    atcBtn.classList.add('is-loading');
    atcBtn.querySelector('.pdp-buy__atc-spinner')?.removeAttribute('hidden');
    const drawer = document.getElementById('CartDrawer');
    if (drawer) {
      const mo = new MutationObserver(() => {
        if (drawer.classList.contains('is-open')) { clearLoading(); mo.disconnect(); }
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      setTimeout(() => { clearLoading(); mo.disconnect(); }, 8000);
    } else {
      setTimeout(clearLoading, 8000);
    }
  };
  if (form && atcBtn) {
    form.addEventListener('submit', (e) => {
      // one request at a time: requestSubmit() from the sticky or header bar
      // ignores the disabled submit button, so the guard lives here
      if (submitting) { e.preventDefault(); e.stopPropagation(); return; }
      // the sticky bar and the header bar submit this same form, so the
      // shared-size check lives here and covers every trigger
      if (tier >= 2 && rows.length) {
        const emptyRows = activeRows().filter((row) => !rowSelection(row).size);
        if (emptyRows.length) {
          e.preventDefault();
          e.stopPropagation();
          showSizeError(emptyRows);
          return;
        }
      } else if (sizeMissing()) {
        // one piece without a size: never add the first variant - land on the picker instead
        e.preventDefault();
        e.stopPropagation();
        showSingleSizeError();
        return;
      }
      submitting = true;
      if (tier < 2 || !rows.length) { setLoading(); return; }
      e.preventDefault();
      e.stopPropagation();
      if (errorEl) { errorEl.textContent = errorDefault; errorEl.hidden = true; }
      setLoading();
      atcBtn.disabled = true;
      (async () => {
        try {
          const grouped = new Map();
          for (const row of activeRows()) {
            const variant = findRowVariant(rowSelection(row));
            if (!variant || !variant.available) throw new Error('variant unavailable');
            grouped.set(variant.id, (grouped.get(variant.id) || 0) + 1);
          }
          const items = [...grouped].map(([id, quantity]) => ({ id, quantity }));
          const opener = document.querySelector('[data-open-cart]');
          const drawer = document.getElementById('CartDrawer');
          if (window.TranquiraCart?.add && drawer) {
            // the drawer controller adds the rows (plus the gift / protection lines it plans for
            // them) in one request, renders from that response and opens itself
            await window.TranquiraCart.add(items, { handle: form.dataset.productHandle });
            if (!drawer.classList.contains('is-open')) clearLoading();
          } else {
            const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items })
            });
            if (!res.ok) throw new Error(await res.text());
            if (opener && drawer) {
              opener.click();
              // the drawer normally opens well within a second; if nothing
              // opens (no cart-drawer.js), refresh the cart badge instead so the
              // add is still visible
              setTimeout(async () => {
                if (drawer.classList.contains('is-open')) return;
                try {
                  const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
                  document.querySelectorAll('[data-cart-count]').forEach((el) => {
                    el.textContent = cart.item_count;
                    el.classList.toggle('is-empty', cart.item_count === 0);
                  });
                } catch { /* badge keeps its value */ }
                clearLoading();
              }, 2500);
            } else {
              window.location.href = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
            }
          }
        } catch (err) {
          console.error('[pdp-tiers]', err);
          if (errorEl) errorEl.hidden = false;
          clearLoading();
        } finally {
          atcBtn.disabled = false;
        }
      })();
    });
  }

  /* ---- size finder (Theme settings > Bundle): the size chart's three
     tables in code - sections/pdp-accordions.liquid stays the reference -
     no network, no invented data. A measurement on a chart boundary gives
     two sizes: both are shown, and in the bundle the suggestion is one of
     each rather than an automatic fill. ---- */
  const finderEl = root.querySelector('[data-pdp-finder]');
  /* 4.4a (2026-09-18): the finder's tables, strings and listeners are wired on the
     first pointer/focus interaction with the finder (pointerdown/focusin run before
     the click that opens it) or at idle time, not inside the boot task. */
  const wireFinder = () => {
    if (!finderEl || !sizeName || finderEl.dataset.wired) return;
    finderEl.dataset.wired = '1';
    let fs = {};
    try { fs = JSON.parse(finderEl.querySelector('[data-finder-strings]')?.textContent || '{}'); } catch { /* strings stay empty */ }
    // the strings arrive with %size% / %band% / %cup% / %a% / %b% placeholders
    // (Liquid cannot nest {{ }} inside an output tag)
    const fill = (key, vars) => Object.entries(vars || {}).reduce(
      (s, [k, v]) => s.split(`%${k}%`).join(String(v)),
      fs[key] || ''
    );
    const TABLES = {
      cm: {
        band: [[68.5, 73.5, 32], [73.5, 78.5, 34], [78.5, 84, 36], [84, 89, 38], [89, 94, 40], [94, 99, 42], [99, 104, 44]],
        cup: [[9, 11.5, 'A'], [11.5, 14, 'B'], [14, 16.5, 'C'], [16.5, 19, 'D'], [19, 21.5, 'DD']]
      },
      in: {
        band: [[27, 29, 32], [29, 31, 34], [31, 33, 36], [33, 35, 38], [35, 37, 40], [37, 39, 42], [39, 41, 44]],
        cup: [[3.5, 4.5, 'A'], [4.5, 5.5, 'B'], [5.5, 6.5, 'C'], [6.5, 7.5, 'D'], [7.5, 8.5, 'DD']]
      }
    };
    const BANDS = [32, 34, 36, 38, 40, 42, 44];
    const GRID = {
      A: ['-', 'S', 'M', '-', '-', '-', '-'],
      B: ['S', 'S', 'M', 'L', 'XL', 'XXL', '3XL'],
      C: ['S', 'M', 'L', 'L', 'XL', 'XXL', '3XL'],
      D: ['S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'],
      DD: ['M', 'L', 'XL', 'XXL', '3XL', '4XL', '4XL']
    };
    const ORDER = ['S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];
    const toggle = finderEl.querySelector('[data-finder-toggle]');
    const panel = finderEl.querySelector('[data-finder-panel]');
    const underEl = finderEl.querySelector('[data-finder-underbust]');
    const bustEl = finderEl.querySelector('[data-finder-bust]');
    const resultEl = finderEl.querySelector('[data-finder-result]');
    let unit = 'cm';

    toggle?.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      if (panel) panel.hidden = !open;
      if (open) underEl?.focus({ preventScroll: true });
    });
    finderEl.addEventListener('pdp:segmented', (e) => {
      if (e.detail.name !== 'finder-units') return;
      unit = e.detail.value === 'in' ? 'in' : 'cm';
      compute();
    });

    const sizePos = () => posOf(sizeName) - 1;
    const sizeKnown = (size) => variants.some((v) => optionValues(v)[sizePos()] === size);
    const sizeAvailable = (size) => variants.some((v) => optionValues(v)[sizePos()] === size && v.available);
    const node = (tag, cls, text) => {
      const n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    };
    // "Your size: <b>M</b>" - the bold size inside the translated template
    const boldLine = (key, size) => {
      const line = node('div', 'pdp-finder__line');
      const [pre, post] = (fs[key] || '%size%').split('%size%');
      line.append(pre || '', node('b', null, size), post || '');
      return line;
    };
    const useButton = (size) => {
      const b = node('button', 'pdp-finder__use', fill('use_size', { size }));
      b.type = 'button';
      b.dataset.finderUse = size;
      return b;
    };
    const chooser = (size) => {
      if (!sizeKnown(size)) return null;
      return sizeAvailable(size) ? useButton(size) : node('p', 'pdp-finder__note', fill('unavailable', { size }));
    };
    const show = (nodes) => {
      const wasHidden = resultEl.hidden;
      resultEl.replaceChildren(...nodes.filter(Boolean));
      resultEl.hidden = nodes.length === 0;
      // a fresh result can land under the sticky add-to-cart bar on a phone:
      // bring it into view once (scroll-margin-bottom in the CSS clears the bar)
      if (wasHidden && nodes.length) {
        requestAnimationFrame(() => resultEl.scrollIntoView({ block: 'nearest', behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' }));
      }
    };

    function compute() {
      if (!resultEl) return;
      const u = parseFloat(underEl?.value);
      const b = parseFloat(bustEl?.value);
      if (!(u > 0) || !(b > 0)) { show([]); return; }
      const t = TABLES[unit];
      const within = (table, v) => table.filter(([lo, hi]) => v >= lo && v <= hi).map((r) => r[2]);
      const bands = within(t.band, u);
      const cups = within(t.cup, b - u);
      const combos = [];
      bands.forEach((band) => cups.forEach((cup) => {
        const size = GRID[cup][BANDS.indexOf(band)];
        if (size !== '-') combos.push({ band, cup, size });
      }));
      const sizes = [...new Set(combos.map((c) => c.size))].sort((a, z) => ORDER.indexOf(a) - ORDER.indexOf(z));
      if (!sizes.length) { show([node('p', 'pdp-finder__note', fs.out_of_range || '')]); return; }
      if (sizes.length === 1) {
        const c = combos[0];
        show([boldLine('result', c.size), node('p', 'pdp-finder__note', fill('detail', { band: c.band, cup: c.cup })), chooser(c.size)]);
        return;
      }
      const a = sizes[0];
      const z = sizes[sizes.length - 1];
      const nodes = [node('div', 'pdp-finder__line', fill('between', { a, b: z }))];
      if (tier > 1 && rows.length) {
        // the bundle can hold one of each: suggest that instead of filling
        nodes.push(node('p', 'pdp-finder__note', fs.between_bundle || ''));
        const choose = node('button', 'pdp-finder__use', fs.choose_sizes || '');
        choose.type = 'button';
        choose.dataset.finderChoose = '1';
        choose.dataset.a = a;
        choose.dataset.b = z;
        nodes.push(choose);
      } else {
        nodes.push(node('p', 'pdp-finder__note', fs.size_up || ''), chooser(z));
      }
      show(nodes);
    }
    underEl?.addEventListener('input', compute);
    bustEl?.addEventListener('input', compute);
    // the boundary advice differs between one piece and the bundle
    tiersEl?.addEventListener('click', () => { if (!resultEl?.hidden) setTimeout(compute, 0); });

    /* ---- "use this size" (your decisions 2026-09-15): every row the shopper
       has not set by hand gets the size (auto-filled rows included); when some
       rows were set by hand an "apply to all" button appears by the rows
       instead of a refusal; the page scrolls back to the rows (instant under
       reduced motion, header accounted for by scroll-margin-top), the rows
       flash, the finder collapses, and the prompt says "now choose the colours". */
    const promptEl = rowsWrap?.querySelector('[data-finder-prompt]');
    const promptText = promptEl?.querySelector('[data-finder-prompt-text]');
    const promptBtn = promptEl?.querySelector('[data-finder-use-all]');
    let promptTimer = 0;
    const hidePrompt = () => {
      if (!promptEl) return;
      clearTimeout(promptTimer);
      promptEl.hidden = true;
      promptEl.classList.remove('is-fading');
      if (promptBtn) promptBtn.hidden = true;
    };
    // with a button (or sticky) the prompt stays until the shopper changes a size; otherwise it fades like the fill hint
    const showPrompt = (text, opts = {}) => {
      if (!promptEl || !promptText) return;
      clearTimeout(promptTimer);
      if (hintEl) hintEl.hidden = true;
      promptText.textContent = text;
      promptEl.classList.remove('is-fading');
      promptEl.hidden = false;
      if (promptBtn) {
        promptBtn.hidden = !opts.button;
        promptBtn.textContent = opts.button || '';
        promptBtn.dataset.finderUseAll = opts.size || '';
      }
      if (opts.button || opts.sticky) return;
      promptTimer = setTimeout(() => {
        if (REDUCED_MOTION.matches) { promptEl.hidden = true; return; }
        promptEl.classList.add('is-fading');
        promptTimer = setTimeout(() => { promptEl.hidden = true; promptEl.classList.remove('is-fading'); }, 450);
      }, 6000);
    };
    const scrollBackTo = (el) => { if (el) el.scrollIntoView({ block: 'start', behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' }); };
    const flash = (els) => {
      els.forEach((el) => el.classList.remove('is-updated'));
      requestAnimationFrame(() => els.forEach((el) => el.classList.add('is-updated'))); // restart without a forced reflow
      setTimeout(() => els.forEach((el) => el.classList.remove('is-updated')), 1600);
    };
    const collapseFinder = () => {
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    };
    const handSet = (i) => touched.has(`${i}:size`);
    // sets the size on the active rows (hand-picked rows only when includeHand); returns the rows that changed
    const applyToRows = (size, includeHand) => {
      const changed = [];
      activeRows().forEach((row, i) => {
        const sel = row.querySelector('select[data-row-option="size"]');
        if (!sel) return;
        if (handSet(i) && !includeHand) return;
        if (![...sel.options].some((o) => o.value === size && !o.disabled)) return;
        if (sel.value !== size) { sel.value = size; changed.push(row); }
        sel.closest('.pdp-bundle__select-wrap')?.classList.remove('is-invalid');
      });
      firstPickDone = true;
      if (errorEl && !errorEl.hidden && errorEl.textContent === errorEl.dataset.sizeRequired && rowSizeSelects().every((s) => s.value)) errorEl.hidden = true;
      return changed;
    };
    const useSize = (size, includeHand) => {
      if (tier > 1 && rows.length) {
        const hand = activeRows().filter((row, i) => handSet(i));
        const changed = applyToRows(size, includeHand);
        const allSet = activeRows().every((row) => rowSelection(row).size === size);
        refresh();
        collapseFinder();
        scrollBackTo(rowsWrap);
        flash(changed);
        if (allSet) showPrompt(fill(includeHand ? 'applied_all' : 'applied_rows', { size }));
        else if (hand.length) showPrompt(fill(changed.length ? 'applied_partial' : 'applied_hand', { size }), { button: fill('apply_all', { size }), size });
        else showPrompt(fill('applied_rows', { size }));
      } else if (sizeSelect) {
        sizeSelect.value = size;
        selected[sizeSelect.dataset.name] = size;
        setPickerLabel(size);
        sizeChosen = true;
        clearSingleSizeError();
        refresh();
        collapseFinder();
        const group = sizeSelect.closest('[data-picker]');
        scrollBackTo(group);
        flash(group ? [group] : []);
      }
    };
    finderEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-finder-use]');
      if (btn) { useSize(btn.dataset.finderUse, false); return; }
      // between two sizes in the bundle: back to the rows with the one-of-each suggestion, nothing filled
      const choose = e.target.closest('[data-finder-choose]');
      if (choose) {
        collapseFinder();
        scrollBackTo(rowsWrap);
        showPrompt(fill('between_prompt', { a: choose.dataset.a, b: choose.dataset.b }), { sticky: true });
      }
    });
    promptBtn?.addEventListener('click', () => useSize(promptBtn.dataset.finderUseAll, true));
    rowsWrap?.addEventListener('change', (e) => { if (e.target.closest('select[data-row-option="size"]')) hidePrompt(); });
  };
  if (finderEl && sizeName) {
    const once = () => { finderEl.removeEventListener('pointerdown', once, true); finderEl.removeEventListener('focusin', once, true); wireFinder(); };
    finderEl.addEventListener('pointerdown', once, true);
    finderEl.addEventListener('focusin', once, true);
    deferInit(wireFinder);
  }

  applyTier(tier);
  // ?tier=3 (announcement / homepage gift links): preselect the 2+1 card. A
  // non-variant parameter: the canonical URL stays the plain product URL.
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tier') === '3' && !params.has('variant') && tier !== 3 && cards.some((c) => c.dataset.tier === '3')) applyTier(3);
  } catch { /* no URL access: keep the default tier */ }
}

/* ------------------------------------------------- generic dot carousels */

function initDotCarousel(track, dotsWrap, slideSelector) {
  if (!track) return;
  const slides = [...track.querySelectorAll(slideSelector)];
  if (slides.length < 2) { if (dotsWrap) dotsWrap.hidden = true; return; }
  let dots = dotsWrap ? [...dotsWrap.children] : [];
  if (dotsWrap && dots.length === 0) {
    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.dataset.index = String(i);
      dot.setAttribute('aria-label', `${i + 1} / ${slides.length}`);
      if (i === 0) dot.classList.add('is-active');
      dotsWrap.appendChild(dot);
    });
    dots = [...dotsWrap.children];
  }
  const setActive = (i) => dots.forEach((d, j) => d.classList.toggle('is-active', i === j));
  dotsWrap?.addEventListener('click', (e) => {
    const dot = e.target.closest('[data-index]');
    if (!dot) return;
    slides[parseInt(dot.dataset.index, 10)]?.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  });
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) setActive(slides.indexOf(entry.target));
    });
  }, { root: track, threshold: 0.6 });
  slides.forEach((s) => io.observe(s));
  return { slides, setActive };
}

/* ----------------------------------------------------- arrows for tracks */

function initArrows(track, prev, next, slideSelector) {
  const step = () => track.querySelector(slideSelector)?.getBoundingClientRect().width || track.clientWidth;
  const behavior = () => (REDUCED_MOTION.matches ? 'auto' : 'smooth');
  prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: behavior() }));
  next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: behavior() }));
  track.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); track.scrollBy({ left: step(), behavior: behavior() }); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); track.scrollBy({ left: -step(), behavior: behavior() }); }
  });
  // no looping: arrows disable at the ends (swipe and click-to-advance stop
  // there too, so all three behave the same)
  if (prev || next) {
    let raf = 0;
    const sync = () => {
      raf = 0;
      const max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    };
    const queueSync = () => { if (!raf) raf = requestAnimationFrame(sync); };
    track.addEventListener('scroll', queueSync, { passive: true });
    window.addEventListener('resize', queueSync, { passive: true });
    sync();
  }
}

/* ---------------------------------------------------------------- gallery */

function initGallery(root) {
  const track = root.querySelector('[data-gallery-track]');
  if (!track) return;
  const slides = [...track.querySelectorAll('[data-gallery-slide]')];
  const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
  const dots = [...root.querySelectorAll('[data-gallery-dot]')];
  const dotsWrap = root.querySelector('[data-gallery-dots]');
  const dotsTrack = root.querySelector('[data-gallery-dots-track]');
  const dotsWindow = parseInt(dotsWrap?.dataset.window, 10) || 5;
  // five-dot window: the active dot sits in the middle (two before, two
  // after) and the window clamps at both ends; the track slides by whole
  // dots. Dots outside the window leave the tab order and the a11y tree.
  // the dot width is read once (and on resize), never between the class writes below
  let dotW = 0;
  const measureDot = () => { dotW = dots[0]?.offsetWidth || 24; };
  if (dots.length) { requestAnimationFrame(measureDot); window.addEventListener('resize', measureDot, { passive: true }); }
  const syncDots = (i) => {
    if (!dots.length) return;
    const total = dots.length;
    const win = Math.min(dotsWindow, total);
    const start = Math.max(0, Math.min(i - Math.floor(win / 2), total - win));
    dots.forEach((d, j) => {
      const on = i === j;
      d.classList.toggle('is-active', on);
      if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      const outside = j < start || j >= start + win;
      d.classList.toggle('is-outside', outside);
      d.tabIndex = outside ? -1 : 0;
      if (outside) d.setAttribute('aria-hidden', 'true'); else d.removeAttribute('aria-hidden');
    });
    if (dotsTrack) dotsTrack.style.setProperty('--dots-x', `${-start * (dotW || 24)}px`);
  };

  initArrows(track, root.querySelector('[data-gallery-prev]'), root.querySelector('[data-gallery-next]'), '[data-gallery-slide]');

  const thumbsEl = root.querySelector('[data-gallery-thumbs]');
  const posEl = root.querySelector('[data-gallery-pos]');

  // manual drag on the strip must not fight auto-centring: a pointer on the
  // strip suspends it, and the next navigation (which calls syncNav) resumes
  let stripDrag = false;
  thumbsEl?.addEventListener('pointerdown', () => { stripDrag = true; }, { passive: true });
  thumbsEl?.addEventListener('touchstart', () => { stripDrag = true; }, { passive: true });

  // active thumb in the centre slot; clamp at the ends so no blank space.
  // offsets are measured relative to the FIRST thumb - offsetLeft alone is
  // relative to an offsetParent that doesn't coincide with the strip.
  // Measured BEFORE syncNav writes any class (one layout, no forced reflow).
  const thumbTarget = (i) => {
    if (!thumbsEl || !thumbs[i] || stripDrag) return null;
    const rel = thumbs[i].offsetLeft - thumbs[0].offsetLeft;
    const target = rel - (thumbsEl.clientWidth - thumbs[i].offsetWidth) / 2;
    const max = thumbsEl.scrollWidth - thumbsEl.clientWidth;
    return Math.min(max, Math.max(0, target));
  };
  const centerThumb = (left, animate) => {
    if (left === null) return;
    thumbsEl.scrollTo({ left, behavior: animate && !REDUCED_MOTION.matches ? 'smooth' : 'auto' });
  };

  // single sync point for EVERY navigation: active class, strip centring,
  // and the "5 / 16" position indicator
  const syncNav = (i, animate = true) => {
    stripDrag = false;
    const left = thumbTarget(i); // reads first ...
    thumbs.forEach((t, j) => {   // ... then the writes
      t.classList.toggle('is-active', i === j);
      t.setAttribute('aria-selected', String(i === j));
    });
    syncDots(i);
    centerThumb(left, animate);
    if (posEl) posEl.textContent = `${i + 1} / ${slides.length}`;
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      syncNav(slides.indexOf(entry.target));
    });
  }, { root: track, threshold: 0.6 });
  slides.forEach((s) => io.observe(s));

  [...thumbs, ...dots].forEach((nav) => {
    nav.addEventListener('click', () => {
      slides[parseInt(nav.dataset.index, 10)]?.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    });
  });

  syncNav(0, false); // correct strip + indicator state on page load

  // advancing helpers shared by tap-to-advance and the swipe assist
  const beh = () => (REDUCED_MOTION.matches ? 'auto' : 'smooth');
  const slideWidth = () => slides[0]?.getBoundingClientRect().width || track.clientWidth || 1;
  const indexNow = () => Math.round(track.scrollLeft / slideWidth());
  const goTo = (i) => {
    const target = Math.min(slides.length - 1, Math.max(0, i));
    track.scrollTo({ left: target * slideWidth(), behavior: beh() });
  };

  // dedicated zoom: opens the lightbox (pinch-zoomable) on the current image;
  // the lightbox code loads on first use
  root.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => {
    const img = slides[indexNow()]?.querySelector('img[data-lightbox-src]');
    if (img) openLightbox(img.dataset.lightboxSrc, img.alt);
  });

  // a short swipe settles on the neighbouring slide instead of snapping back
  // when it falls under the native 50% snap threshold
  let touchX = null;
  let touchIdx = 0;
  track.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1) { touchX = null; return; }
    touchX = e.touches[0].clientX;
    touchIdx = indexNow();
  }, { passive: true });
  track.addEventListener('touchend', (e) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    touchX = null;
    if (Math.abs(dx) < 24) return;
    goTo(touchIdx + (dx < 0 ? 1 : -1));
  }, { passive: true });

  // scroll-linked advance (desktop only, reduced-motion off): the sticky
  // gallery steps through the first 8 images as the product section scrolls.
  // Any manual control - thumb click, swipe, wheel, or a swatch change -
  // suspends it for the rest of the page session.
  if (window.matchMedia('(min-width: 990px)').matches && !REDUCED_MOTION.matches) {
    const MAX_LINKED = 8;
    const linked = slides.slice(0, MAX_LINKED);
    if (linked.length > 1) {
      const frame = root.querySelector('.pdp-gal__frame');
      const wrapper = root.closest('.shopify-section') || root;
      let suspended = false;
      let current = 0;
      let ticking = false;
      let sectionTop = 0;
      let sectionRange = 1;
      let slideW = 1;
      let veil = null;
      const measure = () => {
        const r = wrapper.getBoundingClientRect();
        sectionTop = r.top + window.scrollY;
        sectionRange = Math.max(1, wrapper.offsetHeight - window.innerHeight);
        slideW = track.clientWidth || 1;
      };
      measure();
      window.addEventListener('resize', measure, { passive: true });
      const suspend = () => { suspended = true; };
      track.addEventListener('pointerdown', suspend, { passive: true });
      track.addEventListener('wheel', suspend, { passive: true });
      thumbs.forEach((t) => t.addEventListener('click', suspend));
      document.addEventListener('pdp:gallery:show', suspend); // variant scroll wins
      const crossfadeTo = (i) => {
        const fromImg = linked[current]?.querySelector('img');
        if (fromImg && frame) {
          if (veil) veil.remove();
          veil = document.createElement('div');
          veil.className = 'pdp-gal__veil';
          const clone = fromImg.cloneNode();
          clone.removeAttribute('loading');
          veil.appendChild(clone);
          frame.appendChild(veil);
          requestAnimationFrame(() => veil?.classList.add('is-fading'));
          setTimeout(() => { veil?.remove(); veil = null; }, 450);
        }
        track.scrollLeft = i * slideW; // instant jump beneath the fading veil
        syncNav(i);
      };
      window.addEventListener('scroll', () => {
        if (suspended || ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          if (suspended) return;
          const progress = Math.min(1, Math.max(0, (window.scrollY - sectionTop) / sectionRange));
          const idx = Math.min(linked.length - 1, Math.floor(progress * linked.length));
          if (idx !== current) {
            crossfadeTo(idx);
            current = idx;
          }
        });
      }, { passive: true });
    }
  }

  // colour swatch → variant image (buy box dispatches pdp:gallery:show).
  // detail.index is the ABSOLUTE product.media position - 1; locale-filtered
  // galleries render fewer slides, so resolve by data-media-position first
  // and only fall back to the raw index for media without a position.
  document.addEventListener('pdp:gallery:show', (e) => {
    const want = e.detail?.index;
    const slide = track.querySelector(`[data-media-position="${want + 1}"]`) || slides[want];
    if (!slide) return;
    slide.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    syncNav(slides.indexOf(slide));
  });

  // tapping / clicking the main image advances to the next slide (stops at
  // the last one - same non-looping behaviour as the arrows and swipe)
  track.addEventListener('click', (e) => {
    if (!e.target.closest('[data-gallery-slide]')) return;
    const i = indexNow();
    if (i < slides.length - 1) goTo(i + 1);
  });
}

// the size chart image opens full-screen in the pinch-zoomable lightbox
document.addEventListener('click', (e) => {
  const img = e.target.closest('.pdp-sizechart__img');
  if (img) openLightbox(img.currentSrc || img.src, img.alt);
});

/* -------------------------------------------------------------- accordions */

document.addEventListener('click', (e) => {
  const head = e.target.closest('[data-pdp-accordion] > button');
  if (!head) return;
  const row = head.closest('[data-pdp-accordion]');
  const open = row.classList.toggle('is-open');
  head.setAttribute('aria-expanded', String(open));
});

function openAccordionRow(row) {
  if (!row || row.classList.contains('is-open')) return;
  row.classList.add('is-open');
  row.querySelector(':scope > button')?.setAttribute('aria-expanded', 'true');
}

function initSizeGuide() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pdp-sizeguide]');
    if (!btn) return;
    const row = document.getElementById('pdp-acc-sizechart');
    if (!row) return;
    if (window.matchMedia('(max-width: 989px)').matches) {
      openSheet(row.querySelector('.pdp-inset__content')?.innerHTML || '');
    } else {
      openAccordionRow(row);
      row.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'center' });
    }
  });
}

// size-exchange sticker (snippets/bundle-tier-picker): the conditions live in
// a <template> next to it and open in the same sheet as the size guide
function initExchangeSticker() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-exchange-sticker]');
    if (!btn) return;
    const tpl = btn.parentElement?.querySelector('template[data-exchange-sheet]') || document.querySelector('template[data-exchange-sheet]');
    if (tpl) openSheet(tpl.innerHTML);
  });
}

function wireSizeUnitToggle(scope) {
  scope.addEventListener('pdp:segmented', (e) => {
    if (e.detail.name !== 'size-units') return;
    scope.querySelectorAll('[data-size-table]').forEach((table) => {
      table.hidden = table.dataset.sizeTable !== e.detail.value;
    });
  });
}

function initStickyBar(bar) {
  const atc = document.querySelector('[data-pdp-atc]');
  if (!atc) return;
  let atcVisible = true;
  let drawerOpen = false;

  const update = () => {
    const show = !atcVisible && !drawerOpen;
    if (show) bar.hidden = false;
    requestAnimationFrame(() => bar.classList.toggle('is-visible', show));
  };

  new IntersectionObserver((entries) => {
    atcVisible = entries[0].isIntersecting;
    update();
  }).observe(atc);

  const drawer = document.getElementById('CartDrawer');
  if (drawer) {
    new MutationObserver(() => {
      drawerOpen = drawer.classList.contains('is-open');
      update();
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  bar.querySelector('[data-sticky-atc]')?.addEventListener('click', () => {
    const sizePicker = document.getElementById('pdp-size-picker');
    const sizeSelect = sizePicker?.querySelector('[data-size-select]');
    const multi = parseInt(document.querySelector('[data-pdp-buybox]')?.dataset.tier, 10) > 1;
    if (atc.disabled || atc.classList.contains('is-loading')) return;
    // a missing size is handled by the form's submit guard (scroll, highlight, message)
    void sizePicker; void sizeSelect; void multi;
    const form = document.getElementById('ProductForm');
    if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
  });
}

function initReveals() {
  if (REDUCED_MOTION.matches) return;
  // classes are added here (not in markup) so nothing is hidden without JS.
  // Above-the-fold sections (gallery, buy box) and the fixed-height waves
  // are excluded so LCP and CLS stay untouched.
  const sections = [...document.querySelectorAll('.pdp-bra-template .main > .shopify-section')]
    .filter((w) => !w.matches('.pdp-gal-section, .pdp-buy-section, .pdp-divider-section, .pdp-sticky-section, .pdp-btt-section'));
  const roots = [];
  sections.forEach((w) => {
    const root = w.firstElementChild;
    if (!root || root.getBoundingClientRect().top < window.innerHeight) return;
    [...root.children].forEach((child, i) => {
      child.style.setProperty('--rd', `${Math.min(i, 6) * 60}ms`);
    });
    root.classList.add('pdp-reveal');
    roots.push(root);
  });
  if (!roots.length) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });
  roots.forEach((r) => io.observe(r));
}

function initInCartCount() {
  const el = document.querySelector('[data-in-cart]');
  if (!el) return;
  const productId = Number(el.dataset.productId);
  const template = el.textContent; // localized "(N in cart)"
  const update = async () => {
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      const count = cart.items
        .filter((i) => i.product_id === productId)
        .reduce((sum, i) => sum + i.quantity, 0);
      el.textContent = template.replace(/\d+/, String(count));
      el.hidden = count === 0;
    } catch { /* leave the server-rendered value */ }
  };
  const drawer = document.getElementById('CartDrawer');
  if (drawer) {
    new MutationObserver(() => update()).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }
}

function initWaveCollapse() {
  const isEmptyWrapper = (el) =>
    el && el.classList.contains('shopify-section') &&
    !el.firstElementChild && !el.textContent.trim();
  // sections whose content is full-bleed imagery or video - a gradient fade
  // must never sit against these (it reads as a wash over the photo)
  const MEDIA = '.pdp-gal-section, .pdp-detail-section, .pdp-collage-section, .pdp-ba-section, .pdp-ugc-section';
  const neighbor = (el, dir) => {
    let n = dir === 'next' ? el.nextElementSibling : el.previousElementSibling;
    while (isEmptyWrapper(n)) n = dir === 'next' ? n.nextElementSibling : n.previousElementSibling;
    return n;
  };
  document.querySelectorAll('.main > .pdp-divider-section').forEach((div) => {
    const next = neighbor(div, 'next');
    const prev = neighbor(div, 'prev');
    // orphan: nothing meaningful follows, or another divider does
    if (!next || next.classList.contains('pdp-divider-section')) {
      div.classList.add('is-collapsed');
      return;
    }
    // gradient fades only between two flat-colour sections
    if (div.querySelector('.pdp-fade') &&
        ((next && next.matches(MEDIA)) || (prev && prev.matches(MEDIA)))) {
      div.classList.add('is-collapsed');
    }
  });
}

/* ------------------------------------------------------ lazy module loading
   Two chunks never parse on page load:
   - pdp-overlays.js (lightbox, bottom sheet): imported on the first zoom /
     size-chart / size-guide interaction.
   - pdp-below.js (UGC, detail carousel, before/after, stats, FAQ, back to
     top, waves): imported when a below-the-fold section comes within 600px
     of the viewport, or on the first scroll / touch / key / pointer event.
   Their versioned URLs come from the entry <script> tag's data attributes
   (Shopify's asset_url carries the cache-busting ?v=), so the CDN never
   serves a stale chunk. */

const entryScript = document.querySelector('script[data-pdp-entry]');
const chunkUrl = (name) => entryScript?.dataset[name] || '';

let overlaysPromise = null;
const loadOverlays = () => {
  if (!overlaysPromise) {
    overlaysPromise = import(chunkUrl('overlays')).catch((err) => {
      overlaysPromise = null;
      console.error('[pdp] overlays module failed to load', err);
      throw err;
    });
  }
  return overlaysPromise;
};
function openLightbox(src, alt) {
  loadOverlays().then((m) => m.openLightbox(src, alt)).catch(() => {});
}
function openSheet(html) {
  loadOverlays().then((m) => m.openSheet(html, {
    onMount: (sheet) => {
      sheet.querySelectorAll('[data-pdp-segmented]').forEach(initSegmented);
      wireSizeUnitToggle(sheet);
    }
  })).catch(() => {});
}

let belowPromise = null;
const loadBelow = () => {
  if (!belowPromise) {
    belowPromise = import(chunkUrl('below'))
      .then((m) => m.init({ REDUCED_MOTION, initDotCarousel, initArrows }))
      .catch((err) => { console.error('[pdp] below-the-fold module failed to load', err); });
  }
  return belowPromise;
};
function scheduleBelow() {
  const roots = document.querySelectorAll('[data-pdp-ugc], [data-pdp-carousel], [data-pdp-beforeafter], [data-pdp-stats], [data-pdp-faq], [data-pdp-backtotop], [data-pdp-wave]');
  if (!roots.length) return;
  if (!('IntersectionObserver' in window)) { loadBelow(); return; }
  const io = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    io.disconnect();
    loadBelow();
  }, { rootMargin: '600px 0px' });
  roots.forEach((r) => io.observe(r));
  const onFirstInput = () => { io.disconnect(); loadBelow(); };
  ['scroll', 'touchstart', 'keydown', 'pointerdown', 'wheel'].forEach((ev) => {
    window.addEventListener(ev, onFirstInput, { once: true, passive: true });
  });
}



/* -------------------------------------------------------- promo code copy
   The code chip (snippets/pdp-promo-code) is the copy button. Clipboard API
   in secure contexts, execCommand('copy') fallback elsewhere; on success
   the copy icon swaps to a check for 2s (same 16px slot: no layout shift)
   and the polite live region announces it. If both paths fail the code is
   selected so the visitor can copy it by hand. */
function initPromoCopy(root) {
  const btn = root.querySelector('[data-promo-copy]');
  const codeEl = root.querySelector('[data-promo-code]');
  if (!btn || !codeEl) return;
  const iconCopy = btn.querySelector('.pdp-promo__code-icon--copy');
  const iconCheck = btn.querySelector('.pdp-promo__code-icon--check');
  const live = root.querySelector('[data-promo-live]');
  let timer = 0;

  const legacyCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    ta.remove();
    return ok;
  };
  const copy = async (text) => {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      try { await navigator.clipboard.writeText(text); return true; } catch (e) { /* fall through */ }
    }
    return legacyCopy(text);
  };
  const selectCode = () => {
    const sel = window.getSelection?.();
    if (!sel) return;
    const range = document.createRange();
    range.selectNodeContents(codeEl);
    sel.removeAllRanges();
    sel.addRange(range);
  };
  const setCopied = (on) => {
    btn.classList.toggle('is-copied', on);
    if (iconCopy) iconCopy.hidden = on;
    if (iconCheck) iconCheck.hidden = !on;
    if (live && !on) live.textContent = '';
  };

  btn.addEventListener('click', async () => {
    const ok = await copy(codeEl.textContent.trim());
    if (!ok) { selectCode(); return; }
    setCopied(true);
    if (live) {
      live.textContent = '';
      requestAnimationFrame(() => { live.textContent = btn.dataset.announceCopied || ''; });
    }
    clearTimeout(timer);
    timer = setTimeout(() => setCopied(false), 2000);
  });
}

/* -------------------------------------------------------------------- boot
   Order matters for layout thrash: the gallery measures thumbs and the
   track (reads) before the buy box rewrites the tier rows (writes); the
   reveal pass, which only reads, runs in the next animation frame once
   every init has written, so it costs no extra synchronous layout. */

/* 4.4a (2026-09-18): the boot used to be ONE task (gallery + buy box + everything
   else, ~100 ms of script plus the layouts it forces at 6.5x). It is now split:
   gallery, then the buy box (add to cart, tier picker, price - live within a few
   ms), then the rest, with a scheduler.yield() (setTimeout fallback) between the
   groups so no single task blocks input for long. Wiring that nothing needs before
   the first interaction (size guide, exchange sheet, size finder, in-cart count)
   runs at idle time or on the first pointer/keyboard input, whichever comes first. */
const yieldToMain = () => (window.scheduler && typeof scheduler.yield === 'function')
  ? scheduler.yield()
  : new Promise((resolve) => setTimeout(resolve, 0));
function deferInit(fn) {
  let done = false;
  const run = () => { if (done) return; done = true; fn(); };
  const onInput = () => { ['pointerdown', 'keydown', 'touchstart'].forEach((ev) => window.removeEventListener(ev, onInput, true)); run(); };
  ['pointerdown', 'keydown', 'touchstart'].forEach((ev) => window.addEventListener(ev, onInput, { capture: true, passive: true }));
  (window.requestIdleCallback || ((f) => setTimeout(f, 300)))(run, { timeout: 2000 });
}

(async () => {
  document.querySelectorAll('[data-pdp-segmented]').forEach(initSegmented);
  document.querySelectorAll('[data-pdp-gallery]').forEach(initGallery);
  await yieldToMain();
  document.querySelectorAll('[data-pdp-buybox]').forEach(initBuyBox);
  // theme editor: a re-rendered section comes back as new DOM without handlers - initialise it again
  // (storefront sessions never fire these events)
  if (window.Shopify?.designMode) {
    ['shopify:section:load', 'shopify:section:reorder'].forEach((evt) => document.addEventListener(evt, (e) => {
      (e.target || document).querySelectorAll?.('[data-pdp-buybox]').forEach(initBuyBox);
    }));
  }
  await yieldToMain();
  document.querySelectorAll('[data-pdp-promo]').forEach(initPromoCopy);
  document.querySelectorAll('[data-pdp-stickybar]').forEach(initStickyBar);
  document.querySelectorAll('.pdp-accordions, .pdp-sheet').forEach(wireSizeUnitToggle);
  initWaveCollapse();
  requestAnimationFrame(() => {
    initReveals();
    scheduleBelow();
  });
  deferInit(() => {
    initSizeGuide();
    initExchangeSticker();
    initInCartCount();
  });
})();
